import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Globe,
  DollarSign,
  Users,
  Heart,
  MapPin,
  Building2,
  GraduationCap,
  Clock,
  Plus,
  Trash2,
  Edit,
  Briefcase,
  Wallet,
  Calendar,
  CreditCard,
  FileText,
  TrendingUp,
  Building,
  Landmark,
  FileSignature,
  Mail,
  Upload,
  ChevronDown,
  ChevronRight,
  Settings,
  FolderOpen,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente genérico para CRUD de catálogos simples
const SimpleCatalog = ({ title, icon: Icon, endpoint, fields, data, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [editingId, setEditingId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        await axios.put(`${API}/hr/${endpoint}/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Registro actualizado');
      } else {
        await axios.post(`${API}/hr/${endpoint}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Registro creado');
      }
      setDialogOpen(false);
      setFormData({});
      setEditingId(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hr/${endpoint}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Registro eliminado');
      onRefresh();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setDialogOpen(true);
  };

  return (
    <Card className="mb-4">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className="h-4 w-4 text-purple-600" />
            {title}
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) { setFormData({}); setEditingId(null); }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
                <Plus className="h-3 w-3 mr-1" />
                Agregar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} {title}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {fields.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label>{field.label}</Label>
                    {field.type === 'checkbox' ? (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={formData[field.key] || false}
                          onCheckedChange={(checked) => setFormData({...formData, [field.key]: checked})}
                        />
                        <span className="text-sm text-slate-600">{field.description}</span>
                      </div>
                    ) : field.type === 'number' ? (
                      <Input
                        type="number"
                        step={field.step || "1"}
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData({...formData, [field.key]: parseFloat(e.target.value)})}
                        required={field.required}
                      />
                    ) : (
                      <Input
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                        required={field.required}
                        placeholder={field.placeholder}
                      />
                    )}
                  </div>
                ))}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">
                    {editingId ? 'Actualizar' : 'Guardar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="py-2">
        {data.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No hay registros</p>
        ) : (
          <div className="space-y-1">
            {data.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded hover:bg-slate-100">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                    {item.code}
                  </span>
                  <span className="text-sm">{item.name}</span>
                  {item.symbol && <span className="text-sm text-slate-500">({item.symbol})</span>}
                  {item.hours_per_day && <span className="text-xs text-slate-500">{item.hours_per_day}h/día</span>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(item)}>
                    <Edit className="h-3 w-3 text-slate-500" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const HRCatalogs = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [expandedSection, setExpandedSection] = useState('general');
  
  // Estados para catálogos generales
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [genders, setGenders] = useState([]);
  const [maritalStatuses, setMaritalStatuses] = useState([]);
  const [geoDepartments, setGeoDepartments] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [academicLevels, setAcademicLevels] = useState([]);
  const [workShifts, setWorkShifts] = useState([]);
  
  // Estados para catálogos de empresa
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [deductionTypes, setDeductionTypes] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payrollTypes, setPayrollTypes] = useState([]);
  const [raiseReasons, setRaiseReasons] = useState([]);
  const [positions, setPositions] = useState([]);
  const [companyDepartments, setCompanyDepartments] = useState([]);
  const [payrollBanks, setPayrollBanks] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [contractTemplates, setContractTemplates] = useState([]);
  const [letterTemplates, setLetterTemplates] = useState([]);

  const [loading, setLoading] = useState(true);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Catálogos generales
      const [
        currenciesRes, countriesRes, gendersRes, maritalRes,
        geoDeptsRes, municipalitiesRes, occupationsRes,
        academicRes, shiftsRes
      ] = await Promise.all([
        axios.get(`${API}/hr/currencies`, { headers }),
        axios.get(`${API}/hr/countries`, { headers }),
        axios.get(`${API}/hr/genders`, { headers }),
        axios.get(`${API}/hr/marital-statuses`, { headers }),
        axios.get(`${API}/hr/geo-departments`, { headers }),
        axios.get(`${API}/hr/municipalities`, { headers }),
        axios.get(`${API}/hr/occupations`, { headers }),
        axios.get(`${API}/hr/academic-levels`, { headers }),
        axios.get(`${API}/hr/work-shifts`, { headers }),
      ]);
      
      setCurrencies(currenciesRes.data);
      setCountries(countriesRes.data);
      setGenders(gendersRes.data);
      setMaritalStatuses(maritalRes.data);
      setGeoDepartments(geoDeptsRes.data);
      setMunicipalities(municipalitiesRes.data);
      setOccupations(occupationsRes.data);
      setAcademicLevels(academicRes.data);
      setWorkShifts(shiftsRes.data);
      
      // Catálogos de empresa
      const [
        incomeRes, deductionRes, absenceRes, payMethodRes,
        payrollTypeRes, raiseRes, positionsRes, compDeptsRes,
        banksRes, costCentersRes, contractsRes, lettersRes
      ] = await Promise.all([
        axios.get(`${API}/hr/income-types`, { headers }),
        axios.get(`${API}/hr/deduction-types`, { headers }),
        axios.get(`${API}/hr/absence-types`, { headers }),
        axios.get(`${API}/hr/payment-methods`, { headers }),
        axios.get(`${API}/hr/payroll-types`, { headers }),
        axios.get(`${API}/hr/raise-reasons`, { headers }),
        axios.get(`${API}/hr/positions`, { headers }),
        axios.get(`${API}/hr/company-departments`, { headers }),
        axios.get(`${API}/hr/payroll-banks`, { headers }),
        axios.get(`${API}/hr/cost-centers`, { headers }),
        axios.get(`${API}/hr/contract-templates`, { headers }),
        axios.get(`${API}/hr/letter-templates`, { headers }),
      ]);
      
      setIncomeTypes(incomeRes.data);
      setDeductionTypes(deductionRes.data);
      setAbsenceTypes(absenceRes.data);
      setPaymentMethods(payMethodRes.data);
      setPayrollTypes(payrollTypeRes.data);
      setRaiseReasons(raiseRes.data);
      setPositions(positionsRes.data);
      setCompanyDepartments(compDeptsRes.data);
      setPayrollBanks(banksRes.data);
      setCostCenters(costCentersRes.data);
      setContractTemplates(contractsRes.data);
      setLetterTemplates(lettersRes.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los catálogos');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Catálogos RRHH</h1>
          <p className="text-slate-600 mt-1">Configuración de datos maestros del sistema</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="empresa" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Empresa
          </TabsTrigger>
        </TabsList>

        {/* TAB GENERAL */}
        <TabsContent value="general" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Columna 1 */}
            <div className="space-y-4">
              <SimpleCatalog
                title="Monedas"
                icon={DollarSign}
                endpoint="currencies"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'GTQ' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Quetzal' },
                  { key: 'symbol', label: 'Símbolo', required: true, placeholder: 'Q' },
                ]}
                data={currencies}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Países"
                icon={Globe}
                endpoint="countries"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'GT' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Guatemala' },
                ]}
                data={countries}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Sexo"
                icon={Users}
                endpoint="genders"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'M' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Masculino' },
                ]}
                data={genders}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Estado Civil"
                icon={Heart}
                endpoint="marital-statuses"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'S' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Soltero(a)' },
                ]}
                data={maritalStatuses}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Nivel Académico"
                icon={GraduationCap}
                endpoint="academic-levels"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: '01' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Primaria' },
                  { key: 'order', label: 'Orden', type: 'number', required: false },
                ]}
                data={academicLevels}
                onRefresh={fetchAllData}
              />
            </div>

            {/* Columna 2 */}
            <div className="space-y-4">
              {/* Departamentos Geográficos con selector de país */}
              <Card className="mb-4">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-purple-600" />
                      Departamentos (Geográficos)
                    </CardTitle>
                    <DepartmentDialog 
                      countries={countries} 
                      onSave={fetchAllData}
                    />
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  {geoDepartments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No hay departamentos</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {geoDepartments.map((dept) => {
                        const country = countries.find(c => c.id === dept.country_id);
                        return (
                          <div key={dept.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded">
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {country?.code || '??'}
                              </span>
                              <span className="text-xs font-mono bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                {dept.code}
                              </span>
                              <span className="text-sm">{dept.name}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Municipios con selector de departamento */}
              <Card className="mb-4">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-purple-600" />
                      Municipios
                    </CardTitle>
                    <MunicipalityDialog 
                      departments={geoDepartments}
                      countries={countries}
                      onSave={fetchAllData}
                    />
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  {municipalities.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No hay municipios</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {municipalities.slice(0, 20).map((muni) => {
                        const dept = geoDepartments.find(d => d.id === muni.department_id);
                        return (
                          <div key={muni.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded">
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                {dept?.name?.substring(0, 10) || '??'}
                              </span>
                              <span className="text-sm">{muni.name}</span>
                            </div>
                          </div>
                        );
                      })}
                      {municipalities.length > 20 && (
                        <p className="text-xs text-slate-500 text-center">... y {municipalities.length - 20} más</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ocupaciones con importar */}
              <Card className="mb-4">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-purple-600" />
                      Ocupaciones ({occupations.length})
                    </CardTitle>
                    <OccupationDialog onSave={fetchAllData} />
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  {occupations.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No hay ocupaciones - Importa desde Excel</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {occupations.slice(0, 15).map((occ) => (
                        <div key={occ.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                              {occ.code}
                            </span>
                            <span className="text-sm">{occ.name}</span>
                          </div>
                        </div>
                      ))}
                      {occupations.length > 15 && (
                        <p className="text-xs text-slate-500 text-center">... y {occupations.length - 15} más</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <SimpleCatalog
                title="Jornadas Laborales"
                icon={Clock}
                endpoint="work-shifts"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'D' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Diurna' },
                  { key: 'hours_per_day', label: 'Horas/Día', type: 'number', step: '0.5', required: true },
                  { key: 'description', label: 'Descripción', required: false, placeholder: '6:00 a 18:00' },
                ]}
                data={workShifts}
                onRefresh={fetchAllData}
              />
            </div>
          </div>
        </TabsContent>

        {/* TAB EMPRESA */}
        <TabsContent value="empresa" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Columna 1 */}
            <div className="space-y-4">
              <SimpleCatalog
                title="Tipos de Ingreso"
                icon={TrendingUp}
                endpoint="income-types"
                fields={[
                  { key: 'code', label: 'Código', required: true },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Bonificación' },
                  { key: 'description', label: 'Descripción' },
                  { key: 'is_taxable', label: 'Afecta ISR', type: 'checkbox', description: 'Gravable para ISR' },
                  { key: 'affects_igss', label: 'Afecta IGSS', type: 'checkbox', description: 'Gravable para IGSS' },
                ]}
                data={incomeTypes}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Tipos de Descuento"
                icon={Wallet}
                endpoint="deduction-types"
                fields={[
                  { key: 'code', label: 'Código', required: true },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Préstamo' },
                  { key: 'description', label: 'Descripción' },
                  { key: 'is_legal', label: 'Es de Ley', type: 'checkbox', description: 'Descuento obligatorio por ley' },
                ]}
                data={deductionTypes}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Tipos de Ausencia"
                icon={Calendar}
                endpoint="absence-types"
                fields={[
                  { key: 'code', label: 'Código', required: true },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Vacaciones' },
                  { key: 'description', label: 'Descripción' },
                  { key: 'is_paid', label: 'Con goce de salario', type: 'checkbox', description: 'Se paga durante la ausencia' },
                  { key: 'affects_vacation', label: 'Afecta vacaciones', type: 'checkbox', description: 'Afecta cálculo de vacaciones' },
                ]}
                data={absenceTypes}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Formas de Pago"
                icon={CreditCard}
                endpoint="payment-methods"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'TRF' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Transferencia Bancaria' },
                ]}
                data={paymentMethods}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Tipos de Planilla"
                icon={FileText}
                endpoint="payroll-types"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'TC' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Tiempo Completo' },
                  { key: 'description', label: 'Descripción' },
                ]}
                data={payrollTypes}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Razones de Aumento"
                icon={TrendingUp}
                endpoint="raise-reasons"
                fields={[
                  { key: 'code', label: 'Código', required: true },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Aumento Anual' },
                  { key: 'description', label: 'Descripción' },
                ]}
                data={raiseReasons}
                onRefresh={fetchAllData}
              />
            </div>

            {/* Columna 2 */}
            <div className="space-y-4">
              <SimpleCatalog
                title="Puestos / Cargos"
                icon={Briefcase}
                endpoint="positions"
                fields={[
                  { key: 'code', label: 'Código', required: true },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Vendedor' },
                  { key: 'description', label: 'Descripción' },
                  { key: 'base_salary', label: 'Salario Base Sugerido', type: 'number', step: '0.01' },
                ]}
                data={positions}
                onRefresh={fetchAllData}
              />

              <SimpleCatalog
                title="Departamentos (Empresa)"
                icon={Building}
                endpoint="company-departments"
                fields={[
                  { key: 'code', label: 'Código', required: true },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Ventas' },
                  { key: 'description', label: 'Descripción' },
                ]}
                data={companyDepartments}
                onRefresh={fetchAllData}
              />

              {/* Centros de Costo (solo lectura - usa tiendas) */}
              <Card className="mb-4">
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-purple-600" />
                    Centros de Costo (Tiendas)
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <p className="text-xs text-slate-500 mb-2">Los centros de costo se toman de las tiendas del sistema contable</p>
                  {costCenters.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No hay tiendas creadas</p>
                  ) : (
                    <div className="space-y-1">
                      {costCenters.map((center) => (
                        <div key={center.id} className="flex items-center py-2 px-3 bg-slate-50 rounded">
                          <span className="text-sm">{center.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <SimpleCatalog
                title="Bancos para Planilla"
                icon={Landmark}
                endpoint="payroll-banks"
                fields={[
                  { key: 'code', label: 'Código', required: true, placeholder: 'BI' },
                  { key: 'name', label: 'Nombre', required: true, placeholder: 'Banco Industrial' },
                  { key: 'file_format', label: 'Formato Archivo', required: true, placeholder: 'TXT' },
                  { key: 'separator', label: 'Separador', placeholder: '\\t (tabulación)' },
                ]}
                data={payrollBanks}
                onRefresh={fetchAllData}
              />

              {/* Modelos de Contratos */}
              <Card className="mb-4">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileSignature className="h-4 w-4 text-purple-600" />
                      Modelos de Contratos
                    </CardTitle>
                    <ContractTemplateDialog onSave={fetchAllData} />
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  {contractTemplates.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No hay modelos de contrato</p>
                  ) : (
                    <div className="space-y-1">
                      {contractTemplates.map((tpl) => (
                        <div key={tpl.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded">
                          <span className="text-sm">{tpl.name}</span>
                          <Button size="sm" variant="ghost">
                            <Edit className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Modelos de Cartas */}
              <Card className="mb-4">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="h-4 w-4 text-purple-600" />
                      Modelos de Cartas
                    </CardTitle>
                    <LetterTemplateDialog onSave={fetchAllData} />
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  {letterTemplates.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No hay modelos de carta</p>
                  ) : (
                    <div className="space-y-1">
                      {letterTemplates.map((tpl) => (
                        <div key={tpl.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded">
                          <span className="text-sm">{tpl.name}</span>
                          <Button size="sm" variant="ghost">
                            <Edit className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Componente para agregar Departamentos Geográficos
const DepartmentDialog = ({ countries, onSave }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ country_id: '', code: '', name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr/geo-departments`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Departamento creado');
      setOpen(false);
      setFormData({ country_id: '', code: '', name: '' });
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
          <Plus className="h-3 w-3 mr-1" />
          Agregar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo Departamento</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label>País</Label>
            <select
              className="w-full px-3 py-2 border rounded-md"
              value={formData.country_id}
              onChange={(e) => setFormData({...formData, country_id: e.target.value})}
              required
            >
              <option value="">Seleccionar país</option>
              {countries.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Código</Label>
            <Input
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value})}
              placeholder="01"
              required
            />
          </div>
          <div className="space-y-1">
            <Label>Nombre</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Guatemala"
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">Guardar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Componente para agregar Municipios
const MunicipalityDialog = ({ departments, countries, onSave }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ department_id: '', code: '', name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr/municipalities`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Municipio creado');
      setOpen(false);
      setFormData({ department_id: '', code: '', name: '' });
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
          <Plus className="h-3 w-3 mr-1" />
          Agregar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo Municipio</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label>Departamento</Label>
            <select
              className="w-full px-3 py-2 border rounded-md"
              value={formData.department_id}
              onChange={(e) => setFormData({...formData, department_id: e.target.value})}
              required
            >
              <option value="">Seleccionar departamento</option>
              {departments.map(d => {
                const country = countries.find(c => c.id === d.country_id);
                return (
                  <option key={d.id} value={d.id}>[{country?.code}] {d.name}</option>
                );
              })}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Código</Label>
            <Input
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value})}
              placeholder="0101"
              required
            />
          </div>
          <div className="space-y-1">
            <Label>Nombre</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Guatemala"
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">Guardar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Componente para agregar/importar Ocupaciones
const OccupationDialog = ({ onSave }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('single'); // 'single' o 'bulk'
  const [formData, setFormData] = useState({ code: '', name: '' });
  const [bulkData, setBulkData] = useState('');

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr/occupations`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ocupación creada');
      setFormData({ code: '', name: '' });
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear');
    }
  };

  const handleBulkSubmit = async () => {
    try {
      const lines = bulkData.trim().split('\n');
      const occupations = lines.map(line => {
        const [code, name] = line.split('\t');
        return { code: code?.trim(), name: name?.trim() };
      }).filter(o => o.code && o.name);

      if (occupations.length === 0) {
        toast.error('No se encontraron ocupaciones válidas');
        return;
      }

      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/hr/occupations/bulk`, occupations, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.count} ocupaciones importadas`);
      setBulkData('');
      setOpen(false);
      onSave();
    } catch (error) {
      toast.error('Error al importar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
          <Plus className="h-3 w-3 mr-1" />
          Agregar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Agregar Ocupaciones</DialogTitle>
        </DialogHeader>
        
        <div className="flex gap-2 mb-4">
          <Button 
            variant={mode === 'single' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setMode('single')}
          >
            Individual
          </Button>
          <Button 
            variant={mode === 'bulk' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setMode('bulk')}
          >
            <Upload className="h-3 w-3 mr-1" />
            Importar Masivo
          </Button>
        </div>

        {mode === 'single' ? (
          <form onSubmit={handleSingleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label>Código</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                placeholder="001"
                required
              />
            </div>
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Vendedor"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">Guardar</Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Pega el contenido de Excel (Código + TAB + Nombre), una ocupación por línea:
            </p>
            <textarea
              className="w-full h-48 px-3 py-2 border rounded-md font-mono text-sm"
              placeholder="001&#9;Vendedor&#10;002&#9;Cajero&#10;003&#9;Gerente"
              value={bulkData}
              onChange={(e) => setBulkData(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={handleBulkSubmit} className="bg-purple-600 hover:bg-purple-700 text-white">
                Importar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Componente para crear Modelos de Contratos
const ContractTemplateDialog = ({ onSave }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', content: '', variables: [] });
  const [availableVars, setAvailableVars] = useState(null);

  const fetchVariables = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/hr/template-variables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableVars(res.data);
    } catch (error) {
      console.error('Error fetching variables:', error);
    }
  };

  useEffect(() => {
    if (open && !availableVars) {
      fetchVariables();
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr/contract-templates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modelo de contrato creado');
      setOpen(false);
      setFormData({ name: '', content: '', variables: [] });
      onSave();
    } catch (error) {
      toast.error('Error al crear');
    }
  };

  const insertVariable = (varKey) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + varKey
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
          <Plus className="h-3 w-3 mr-1" />
          Crear
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo Modelo de Contrato</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label>Nombre del Modelo</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Contrato Indefinido"
              required
            />
          </div>
          
          {availableVars && (
            <div className="space-y-2">
              <Label>Variables Disponibles (clic para insertar)</Label>
              <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded max-h-40 overflow-y-auto">
                {Object.entries(availableVars).map(([category, vars]) => (
                  <div key={category}>
                    <p className="text-xs font-semibold text-slate-600 mb-1">{category}</p>
                    {vars.map(v => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => insertVariable(v.key)}
                        className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded mr-1 mb-1 hover:bg-purple-200"
                        title={v.description}
                      >
                        {v.key}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label>Contenido del Contrato</Label>
            <textarea
              className="w-full h-64 px-3 py-2 border rounded-md font-mono text-sm"
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              placeholder="CONTRATO DE TRABAJO&#10;&#10;Entre la empresa {{empresa_nombre}}, representada por {{representante_legal}}, y el trabajador {{nombre_completo}}, con DPI {{dpi}}..."
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">Guardar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Componente para crear Modelos de Cartas (similar a contratos)
const LetterTemplateDialog = ({ onSave }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', content: '', variables: [] });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr/letter-templates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modelo de carta creado');
      setOpen(false);
      setFormData({ name: '', content: '', variables: [] });
      onSave();
    } catch (error) {
      toast.error('Error al crear');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
          <Plus className="h-3 w-3 mr-1" />
          Crear
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Nuevo Modelo de Carta</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label>Nombre del Modelo</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Constancia Laboral"
              required
            />
          </div>
          <div className="space-y-1">
            <Label>Contenido de la Carta</Label>
            <textarea
              className="w-full h-48 px-3 py-2 border rounded-md"
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              placeholder="A quien corresponda:&#10;&#10;Por medio de la presente hacemos constar que {{nombre_completo}}..."
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">Guardar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default HRCatalogs;
